"use client"
import "./ModelSelector.css"

const ModelSelector = ({ selectedModels, onChange, hcref, onHcrefChange }) => {
  const generalModel = {
    id: "general-penman-monteith",
    name: "KSU Penman-Monteith",
    description: "General Penman-Monteith equation for reference evapotranspiration",
  }

  const alfalfaModels = [
    {
      id: "standardized-asce-penman-monteith",
      name: "ASCE Penman-Monteith",
      description: "Standard method for alfalfa reference evapotranspiration",
    },
    {
      id: "original-penman",
      name: "Original Penman-Monteith",
      description: "Classic evaporation model modified for alfalfa",
    },
    {
      id: "kimberly-1982-penman",
      name: "Kimberly 1982 Penman-Monteith",
      description: "Refined Penman model developed at Kimberly, Idaho",
    },
    {
      id: "jensen-haise",
      name: "Jensen-Haise Method",
      description: "Temperature-based model for alfalfa reference ET",
    },
  ]

  const grassModels = [
    {
      id: "fao-56-penman-monteith",
      name: "FAO-56 Penman Monteith Model",
      description: "International standard for grass reference ET",
    },
    {
      id: "fao-24-penman",
      name: "FAO-24 Penman Model",
      description: "FAO adaptation of Penman equation",
    },
    {
      id: "turc-1961",
      name: "Turc Method",
      description: "Simplified model for humid climates",
    },
    {
      id: "fao-blaney-criddle",
      name: "FAO Blaney-Criddle Method",
      description: "Temperature-based model with daylight adjustment",
    },
    {
      id: "hargreaves-1985",
      name: "Hargreaves 1985 Method",
      description: "Temperature-based model with radiation estimation",
    },
    {
      id: "fao-24-pan-evaporation",
      name: "FAO-24 PAN Evaporation Calculation",
      description: "Based on measured pan evaporation with coefficients",
    },
  ]

  const handleModelChange = (category, modelId, e) => {
    onChange(category, modelId, e.target.checked)
  }

  const handleGeneralModelChange = (e) => {
    onChange("general", generalModel.id, e.target.checked)
  }

  const handleCategoryChange = (category, e) => {
    const isChecked = e.target.checked
    const models = category === "alfalfa" ? alfalfaModels : grassModels

    // Select/deselect all models in the category
    models.forEach((model) => {
      onChange(category, model.id, isChecked)
    })
  }

  const handleHcrefChange = (e) => {
    const value = Number.parseFloat(e.target.value) || 0.2
    onHcrefChange(value)
  }

  // Check if all models in a category are selected
  const isAlfalfaCategorySelected = alfalfaModels.every((model) => selectedModels.alfalfa?.includes(model.id))

  const isGrassCategorySelected = grassModels.every((model) => selectedModels.grass?.includes(model.id))

  // Check if some models in a category are selected (for indeterminate state)
  const isAlfalfaCategoryIndeterminate = selectedModels.alfalfa?.length > 0 && !isAlfalfaCategorySelected

  const isGrassCategoryIndeterminate = selectedModels.grass?.length > 0 && !isGrassCategorySelected

  return (
    <div className="model-selector">
      <div className="model-icon">
        <i className="fas fa-calculator"></i>
        <span>Select WATref Model(s)</span>
      </div>

      <div className="models-container">
        {/* General Penman-Monteith Model */}
        <div className="general-model-section">
          <div className="general-model-item">
            <label className="checkbox-container">
              <input
                type="checkbox"
                checked={selectedModels.general?.includes(generalModel.id) || false}
                onChange={handleGeneralModelChange}
              />
              <span className="model-name">
                {generalModel.name}
                <i className="fas fa-info-circle model-info-icon" title={generalModel.description}></i>
              </span>
            </label>
            <div className="hcref-input-container">
              <span>hcref [m]:</span>
              <input
                type="number"
                value={hcref}
                onChange={handleHcrefChange}
                step="0.1"
                min="0"
                max="10"
                className="hcref-input"
                placeholder="0.2"
                title="Reference crop height (hcref) in meters"
              />
            </div>

          </div>
        </div>

        <div className="model-categories">
          <div className="model-category">
            <div className="category-header">
              <input
                type="checkbox"
                id="alfalfa-category"
                className="category-checkbox"
                checked={isAlfalfaCategorySelected}
                ref={(checkbox) => {
                  if (checkbox) checkbox.indeterminate = isAlfalfaCategoryIndeterminate
                }}
                onChange={(e) => handleCategoryChange("alfalfa", e)}
              />
              <label htmlFor="alfalfa-category" className="category-title">
                Tall Crop (Alfalfa)
              </label>
            </div>
            <div className="model-list">
              {alfalfaModels.map((model) => (
                <div key={model.id} className="model-item">
                  <label className="checkbox-container">
                    <input
                      type="checkbox"
                      checked={selectedModels.alfalfa?.includes(model.id) || false}
                      onChange={(e) => handleModelChange("alfalfa", model.id, e)}
                    />
                    <span className="model-name">
                      {model.name}
                      <i className="fas fa-info-circle model-info-icon" title={model.description}></i>
                    </span>
                  </label>
                </div>
              ))}
            </div>
          </div>

          <div className="model-category">
            <div className="category-header">
              <input
                type="checkbox"
                id="grass-category"
                className="category-checkbox"
                checked={isGrassCategorySelected}
                ref={(checkbox) => {
                  if (checkbox) checkbox.indeterminate = isGrassCategoryIndeterminate
                }}
                onChange={(e) => handleCategoryChange("grass", e)}
              />
              <label htmlFor="grass-category" className="category-title">
                Short Crop (Grass)
              </label>

            </div>
            <div className="model-list">
              {grassModels.map((model) => (
                <div key={model.id} className="model-item">
                  <label className="checkbox-container">
                    <input
                      type="checkbox"
                      checked={selectedModels.grass?.includes(model.id) || false}
                      onChange={(e) => handleModelChange("grass", model.id, e)}
                    />
                    <span className="model-name">
                      {model.name}
                      <i className="fas fa-info-circle model-info-icon" title={model.description}></i>
                    </span>
                  </label>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ModelSelector
